import { EntityIconType, EntityIconMap } from '@common/Entity/constants';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, getSqlIDEUrl } from '@common/url';
import { titleCase } from '@common/utils/metadataUtils';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { ArrowNarrowUpRightIcon, SearchSmIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { ColumnsType, Stack, Table, TableActions, TableIconButton } from '@prophecy/ui-v3';
import { FilterType } from '@prophecy/ui-v3/Table/types';
import { caseInsensitiveInclude, isCaseInsensitiveEqual } from '@prophecy/utils/string';
import { startCase } from 'lodash-es';
import React, { useMemo, useState } from 'react';

import { PackageDetailStatusKeys, SourceInfoType, StatusMapKeys } from '../../../../data/apis/transpileTypes';
import { ComponentDetail } from '../../components/ComponentDetail';
import { FileFormats, StatusMap } from '../../config';
import { SearchInput } from '../../Import/steps/utils';
import { hasStatusWithError, InfoTableHeader, StyledNameContainer } from '../util';

export default function SourceTable({
  sources,
  loading,
  type,
  isSql,
  transpilerId,
  projectName,
  projectId
}: {
  sources: SourceInfoType[];
  loading?: boolean;
  isSql?: boolean;
  transpilerId: string;
  type: string;
  projectId?: string;
  projectName?: string;
}) {
  const [selectedComponentName, setComponentName] = useState('');
  const [searchName, onChange] = useState('');
  const columns: ColumnsType<SourceInfoType> = [
    {
      key: 'entityName',
      dataIndex: 'entityName',
      title: 'Entity',
      render: (name: string, rowData) => {
        const isSP = isCaseInsensitiveEqual(rowData.entity, Entity.StoredProcedure);
        const entity = (isSP ? Entity.StoredProcedure : titleCase(rowData.entity)) as EntityIconType;
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
            <EntityIcon entity={EntityIconMap[entity] ? entity : Entity.Gem} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
          </Stack>
        );
      }
    },
    {
      key: 'parentEntity',
      dataIndex: 'parentEntity',
      title: 'Parent Entity',
      render: renderColumnWithEllipsis,
      sortable: true
    },
    {
      key: 'source',
      dataIndex: 'source',
      title: 'Source File',
      render: renderColumnWithEllipsis,
      sortable: true
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: 'Message',
      render: (msg) => renderColumnWithEllipsis(msg, undefined, undefined, true)
    },
    {
      key: 'status',
      dataIndex: 'status',
      filter: {
        type: FilterType.list,
        options: PackageDetailStatusKeys.filter(
          (value) => ![StatusMapKeys.built, StatusMapKeys.read, StatusMapKeys.unknown].includes(value)
        ).map((value) => ({ label: titleCase(value), value }))
      },
      title: 'Status',
      render: (status: string, row) => {
        const entity = row.entity;
        const isTranspiledEntity =
          isCaseInsensitiveEqual(entity, Entity.Pipeline) ||
          isCaseInsensitiveEqual(entity, Entity.Job) ||
          isCaseInsensitiveEqual(entity, Entity.StoredProcedure);
        const isFailed = isCaseInsensitiveEqual(status, StatusMapKeys.failed);
        const currentStatus = titleCase(startCase(status.toLowerCase()));
        return (
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
            {StatusMap[status.toLowerCase() as StatusMapKeys]}
            <StyledNameContainer>{renderColumnWithEllipsis(currentStatus)}</StyledNameContainer>
            {isTranspiledEntity && !isFailed && (
              <StyledNameContainer grow='1'>{row.coveragePercent}%</StyledNameContainer>
            )}
          </Stack>
        );
      }
    },
    {
      key: 'actions',
      dataIndex: 'actions',
      width: '60px',
      render: (_, row) => {
        const isPipeline = isCaseInsensitiveEqual(row.entity, Entity.Pipeline);
        const isJob = isCaseInsensitiveEqual(row.entity, Entity.Job);
        if (!isPipeline && !isJob) {
          return;
        }
        const isTranspiledEntity =
          isPipeline ||
          isCaseInsensitiveEqual(row.entity, Entity.Job) ||
          isCaseInsensitiveEqual(row.entity, Entity.Model);
        if (hasStatusWithError(row.status) && isTranspiledEntity) {
          return null;
        }

        const name = row.entityName;
        const entityId = row.entityId;

        let actionUI = null;

        const getCurrentUrl = (entity: string, subEntity = '') => {
          let params: { entity: string; name?: string; id?: string; table?: string; transpilerGemId?: string } = {
            entity: entity.toLowerCase(),
            ...(isSql && isJob ? { id: entityId } : { name })
          };
          if (isCaseInsensitiveEqual(subEntity, Entity.Table)) {
            params = { ...params, entity: subEntity.toLowerCase(), table: name };
            delete params.name;
          } else if (isCaseInsensitiveEqual(subEntity, Entity.Gem)) {
            params = { ...params, name: row.entityId, transpilerGemId: name };
          }
          let currentUrl =
            isSql && projectId
              ? getSqlIDEUrl({ uid: projectId, ...params })
              : getIDEUrl(
                  {
                    uid: entityId,
                    entity: isPipeline || isCaseInsensitiveEqual(entity, Entity.Pipeline) ? Entity.Pipeline : Entity.Job
                  },
                  isCaseInsensitiveEqual(row.entity, Entity.Gem) ? { transpilerGemId: name } : undefined
                );

          let currentActionUI = null;
          if ((isSql && projectId) || (!isSql && entityId)) {
            currentActionUI = (
              <TableIconButton
                icon={<ArrowNarrowUpRightIcon size='s' color={theme.colors.gray500} type='default' />}
                elementType='link'
                to={currentUrl}
                aria-label={`View ${name} details`}
                data-test-id='source-table-view-details-button'
              />
            );
          }

          return { currentActionUI };
        };

        if (isTranspiledEntity) {
          const { currentActionUI } = getCurrentUrl(row.entity);
          actionUI = currentActionUI;
        } else if (type !== FileFormats.snaplogic) {
          const parentEntity = row.parentEntityType;
          const { currentActionUI } = getCurrentUrl(parentEntity, row.entity);
          actionUI = (
            <>
              {name === selectedComponentName && (
                <ComponentDetail
                  pipelineName={row.parentEntity}
                  onClose={() => setComponentName('')}
                  componentId={name}
                  projectName={projectName}
                  transpilerId={transpilerId}
                />
              )}
              <TableIconButton
                onClick={() => setComponentName(name)}
                icon={<SearchSmIcon type='default' color={theme.colors.gray400} />}
                aria-label={`Search ${name} details`}
                data-test-id='source-table-search-button'
              />
              {parentEntity && currentActionUI}
            </>
          );
        }

        return (
          <TableActions gap={theme.spaces.x4} align='end' alignY='center' direction='horizontal'>
            {actionUI}
          </TableActions>
        );
      }
    }
  ];
  const rowData = useMemo(() => {
    let filteredData = sources.map((row, i) => ({ ...row, key: i + 'pipeline' }));
    if (searchName) {
      filteredData = filteredData.filter((row) => caseInsensitiveInclude(row.entityName, searchName));
    }
    return filteredData;
  }, [searchName, sources]);
  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x16}>
      <Stack
        padding={`${theme.spaces.x32} 0 0`}
        gap={theme.spaces.x16}
        align='space-between'
        alignY='center'
        direction='horizontal'
        width='100%'>
        <InfoTableHeader title='Entities' count={rowData.length} />
        <SearchInput value={searchName} placeholder='Search for entities...' onChange={onChange} />
      </Stack>
      <Stack grow='1'>
        <Table
          virtualize={true}
          fixedHeader={true}
          loading={loading}
          height='100%'
          width='100%'
          columns={columns}
          dataSource={rowData}
          data-test-id='imported-info-source-entities-table'
        />
      </Stack>
    </Stack>
  );
}
